import * as Sentry from '@sentry/browser';
import Symbol from 'es6-symbol';

import { SET_SELF_SERVICE_ERROR_EVENT_ID } from './apiError.types';
import { SHOW_TOAST_MESSAGE, TOAST_MESSAGE_SEVERITY_WARNING } from 'modules/layout/types';
import Log from 'utilities/log';
import auth from 'auth/auth';
import history from 'browserHistory';

export const INTAKE_API_BASE_URL = process.env.REACT_APP_INTAKE_API_V2_URL || 'https://api-reloinsight-v2-test.trcgm.com/';

function callApi(token, endpoint, authenticated, method = 'GET', payload, responseType = 'json') {
  const baseUrl = INTAKE_API_BASE_URL;
  let config = { headers: {} };

  if (authenticated) {
    if (token) {
      config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
    } else {
      Log.trace('No token available.');
      throw new Error('No token saved!');
    }
  }

  config.method = method;

  if (typeof payload !== 'undefined') {
    if (Object.prototype.toString.call(payload) === '[object FormData]') {
      config.body = payload;
    } else {
      config.body = JSON.stringify(payload);
      config.headers['Content-Type'] = 'application/json; charset=utf-8';
    }
  }

  const parseBody = async response => {
    try {
      if (responseType === 'blob') return await response.blob();
      if (responseType === 'text') return await response.text();
      if (responseType === 'arrayBuffer') return await response.arrayBuffer();
      if (responseType === 'raw') return null; // caller uses raw response only
      return await response.json(); // default
    } catch (e) {
      // keep old behavior-ish fallback
      return responseType === 'json' ? [] : null;
    }
  };

  return fetch(baseUrl + endpoint, config)
    .then(async response => {
      const body = await parseBody(response);
      return { body, response };
    })
    .then(({ body, response }) => {
      if (response.status === 401) {
        return Promise.reject({ authenticationRequired: true });
      }

      if (response.status === 409) {
        return Promise.reject({ isCollision: true, message: body?.message });
      }

      if (!response.ok) {
        // for non-json error bodies
        if (responseType !== 'json') {
          return Promise.reject({
            message: typeof body === 'string' && body ? body : 'There was an error.',
            status: response.status
          });
        }
        return Promise.reject(body);
      }

      const metadata = {};
      if (response.headers.get('X-Total-Count')) {
        metadata.totalCount = Number(response.headers.get('X-Total-Count'));
      }

      return { data: body, metadata, rawResponse: response };
    });
}
export const CALL_INTAKE_V2_API = Symbol('Call Intake V2 API');

export default ({ dispatch, getState }) => next => action => {
  const callAPI = action[CALL_INTAKE_V2_API];

  // So the middleware doesn't get applied to every single action
  if (typeof callAPI === 'undefined') {
    return next(action);
  }

  let { endpoint, types, authenticated, method, payload, options, context, responseType, suppressCollisionToast } = callAPI;

  const [requestType, successType, errorType] = types;

  next({
    type: requestType,
    payload
  });

  let jwt;
  if (authenticated) {
    try {
      jwt = auth.getAccessToken();
    } catch (e) {
      Log.trace('ERROR GETTING TOKEN', e);
    }
  }

  // Passing the authenticated boolean back in our data will let us distinguish
  // between normal and secret quotes
  return callApi(jwt, endpoint, authenticated, method, payload, responseType).then(
    response => {
      return next({
        response: response.data,
        //metadata: response.metadata,
        rawResponse: response.rawResponse,
        authenticated,
        payload,
        type: successType,
        options,
        context
      });
    },
    async (error) => {
      // Log the full error object for debugging
      console.error('INTAKE V2 API ERROR - Full error object:', error);
      console.error('INTAKE V2 API ERROR - Error type:', typeof error);
      console.error('INTAKE V2 API ERROR - Error keys:', error ? Object.keys(error) : 'null');
      if (error && error.title) console.error('INTAKE V2 API ERROR - Title:', error.title);
      if (error && error.detail) console.error('INTAKE V2 API ERROR - Detail:', error.detail);
      if (error && error.errors) console.error('INTAKE V2 API ERROR - Errors:', error.errors);
      
      const resp = {
        type: errorType,
        context
      };
      if (error.messages) {
        resp.messages = error.messages;
      } else if (error.message) {
        resp.messages = [error.message];
      } else {
        resp.messages = ['There was an error.'];
      }

      Sentry.captureException(new Error(`API Error: ${JSON.stringify(resp)}`));
      dispatch({ type: SET_SELF_SERVICE_ERROR_EVENT_ID, payload: error.eventId });

      // Endpoints whose 409 is a business-rule conflict (not a stale rowversion) opt out and show error.message themselves
      if (error.isCollision && !suppressCollisionToast) {
        dispatch({
          type: SHOW_TOAST_MESSAGE,
          response: {
            message: "Your changes will overwrite another user's, please refresh to get the latest data before saving.",
            userMustDismiss: true,
            severity: TOAST_MESSAGE_SEVERITY_WARNING,
          }
        });
      }

      const nextRes = next(resp);
      if (error.authenticationRequired) {
        const wasRenewed = await auth.renewSession();
        if (!wasRenewed) {
          history.replace('/login', {
            from: { pathname: history.location.pathname }
          });
        }
      }
      return nextRes;
    }
  );
};
